import { jsPDF } from "jspdf";
import { ragConfig, startCopy } from "@/data/diagnostic";
import { contactInfo } from "@/data/mock";

const TEAL_DARK = [3, 46, 45];
const TEAL = [14, 141, 134];
const BROWN = [168, 101, 50];
const INK = [28, 25, 23];
const GREY = [120, 113, 108];
const CREAM = [250, 247, 242];

const RAG_COLORS = {
  red: [220, 90, 80],
  amber: [230, 160, 60],
  green: [50, 160, 110],
};

export function generateSnapshotPdf({ snapshot, interp, qual, firstName, businessName }) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const W = 210;
  const M = 18;
  let y = 0;

  const ensureSpace = (needed) => {
    if (y + needed > 275) {
      doc.addPage();
      y = 20;
    }
  };

  const dateStr = new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

  // Header band
  doc.setFillColor(...TEAL_DARK);
  doc.rect(0, 0, W, 42, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.text("TEQIE", M, 16);
  doc.setFontSize(15);
  doc.text("Growth & Efficiency Snapshot", M, 27);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.5);
  doc.setTextColor(180, 225, 220);
  doc.text(`${businessName || "Your business"}  ·  ${dateStr}`, M, 35);

  y = 52;

  doc.setTextColor(...GREY);
  doc.setFontSize(9.5);
  const intro = doc.splitTextToSize(
    "This snapshot identifies patterns, not definitive causes. The value is in seeing which areas are supporting the business and which may be quietly constraining it.",
    W - 2 * M
  );
  doc.text(intro, M, y);
  y += intro.length * 4.4 + 6;

  // Dimensions
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(...INK);
  doc.text("Your seven dimensions", M, y);
  y += 7;

  const barX = M + 62;
  const barW = 70;
  snapshot.dimensions.forEach((d) => {
    const ragColor = RAG_COLORS[d.rag];
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    doc.setTextColor(...INK);
    doc.text(d.label, M, y + 4);
    doc.setFillColor(232, 228, 220);
    doc.roundedRect(barX, y + 1, barW, 3.6, 1.8, 1.8, "F");
    doc.setFillColor(...ragColor);
    doc.roundedRect(barX, y + 1, Math.max(4, (d.average / 5) * barW), 3.6, 1.8, 1.8, "F");
    doc.setFont("helvetica", "bold");
    doc.text(`${d.average.toFixed(1)}`, barX + barW + 5, y + 4);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...ragColor);
    doc.text(ragConfig[d.rag].label, barX + barW + 14, y + 4);
    y += 9.5;
  });
  y += 4;

  // Strongest / focus highlight cards
  const byKey = Object.fromEntries(snapshot.dimensions.map((d) => [d.key, d]));
  const highlights = [
    { eyebrow: "STRONGEST AREA", d: byKey[snapshot.strongest], color: RAG_COLORS.green },
    { eyebrow: "MOST WORTH EXPLORING", d: byKey[snapshot.focus], color: BROWN },
  ];
  const cardW = (W - 2 * M - 5) / 2;
  highlights.forEach((h, i) => {
    const x = M + i * (cardW + 5);
    doc.setFillColor(...CREAM);
    doc.setDrawColor(225, 220, 210);
    doc.roundedRect(x, y, cardW, 20, 2.5, 2.5, "FD");
    doc.setFontSize(6.8);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...h.color);
    doc.text(h.eyebrow, x + 4, y + 6);
    doc.setFontSize(10);
    doc.setTextColor(...INK);
    doc.text(`${h.d.label}  —  ${h.d.average.toFixed(1)} / 5`, x + 4, y + 13);
  });
  y += 28;

  // Primary pattern
  const primary = interp && interp.primary_hypothesis;
  if (primary) {
    const sumLines = doc.splitTextToSize(primary.summary, W - 2 * M - 8);
    const headLines = doc.splitTextToSize(primary.heading, W - 2 * M - 8);
    const evLines = interp.evidence_plain ? doc.splitTextToSize(interp.evidence_plain, W - 2 * M - 8) : [];
    const blockH = 12 + headLines.length * 5 + sumLines.length * 4.4 + (evLines.length ? evLines.length * 4 + 3 : 0) + 4;
    ensureSpace(blockH + 4);
    doc.setDrawColor(...BROWN);
    doc.setFillColor(252, 248, 243);
    doc.roundedRect(M, y, W - 2 * M, blockH, 3, 3, "FD");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7);
    doc.setTextColor(...BROWN);
    doc.text("A PATTERN WORTH EXPLORING", M + 4, y + 6);
    doc.setFontSize(11.5);
    doc.setTextColor(...INK);
    doc.text(headLines, M + 4, y + 12.5);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(60, 56, 52);
    doc.text(sumLines, M + 4, y + 13 + headLines.length * 5);
    if (evLines.length) {
      doc.setFontSize(8.2);
      doc.setTextColor(...GREY);
      doc.text(evLines, M + 4, y + 15 + headLines.length * 5 + sumLines.length * 4.4);
    }
    y += blockH + 8;
  }

  // 90-day priority
  const ninety = ((qual && qual.ninety_days) || "").trim();
  if (ninety) {
    const nLines = doc.splitTextToSize(`"${ninety}"`, W - 2 * M - 8);
    ensureSpace(nLines.length * 4.4 + 14);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7);
    doc.setTextColor(...TEAL);
    doc.text("YOUR 90-DAY PRIORITY, IN YOUR WORDS", M, y);
    y += 5;
    doc.setFont("helvetica", "italic");
    doc.setFontSize(9.5);
    doc.setTextColor(...INK);
    doc.text(nLines, M, y);
    y += nLines.length * 4.4 + 7;
  }

  // Where I'd start (dark block) + explore questions
  const whereText = (interp && interp.where_id_start) || startCopy[snapshot.focus];
  const startLines = doc.splitTextToSize(whereText, W - 2 * M - 12);
  const exploreQs = ((interp && interp.exploration_questions) || []).slice(0, 3);
  const qLines = exploreQs.map((q) => doc.splitTextToSize(`•  ${q}`, W - 2 * M - 14));
  const qTotal = qLines.reduce((a, l) => a + l.length, 0);
  const blockH = 16 + startLines.length * 4.6 + (exploreQs.length ? 9 + qTotal * 4.4 : 0) + 4;
  ensureSpace(blockH + 4);
  doc.setFillColor(...TEAL_DARK);
  doc.roundedRect(M, y, W - 2 * M, blockH, 3, 3, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(255, 255, 255);
  doc.text("Where I'd start", M + 6, y + 9);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.5);
  doc.setTextColor(210, 240, 235);
  doc.text(startLines, M + 6, y + 16);
  if (exploreQs.length) {
    let qy = y + 18 + startLines.length * 4.6;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7);
    doc.setTextColor(120, 200, 190);
    doc.text("QUESTIONS WORTH EXPLORING", M + 6, qy);
    qy += 5;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.8);
    doc.setTextColor(200, 235, 230);
    qLines.forEach((lines) => {
      doc.text(lines, M + 6, qy);
      qy += lines.length * 4.4;
    });
  }
  y += blockH + 10;

  // Next steps / contact
  ensureSpace(60);
  doc.setDrawColor(...TEAL);
  doc.setLineWidth(0.6);
  doc.line(M, y, W - M, y);
  y += 8;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...INK);
  doc.text("What happens next", M, y);
  y += 6;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(60, 56, 52);
  const next = doc.splitTextToSize(
    "I review every diagnostic personally. If you decide to book a Growth Conversation, I'll review your responses beforehand — so we can spend our time exploring what's behind the results and what might be worth doing next. Your diagnostic tells us where to look; the next step is understanding why.",
    W - 2 * M
  );
  doc.text(next, M, y);
  y += next.length * 4.2 + 7;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(9.5);
  doc.setTextColor(...TEAL_DARK);
  doc.text("Shakirah Akinwale  ·  Founder & Principal Consultant, Teqie Ltd", M, y);
  y += 6;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.8);
  doc.setTextColor(...GREY);
  doc.text("Email:", M, y);
  doc.setTextColor(...TEAL);
  doc.textWithLink(contactInfo.email, M + 12, y, { url: `mailto:${contactInfo.email}` });
  y += 5;
  doc.setTextColor(...GREY);
  doc.text("WhatsApp:", M, y);
  doc.setTextColor(...TEAL);
  doc.textWithLink("+44 7599 208763", M + 18, y, { url: contactInfo.whatsapp });
  y += 5;
  doc.setTextColor(...GREY);
  doc.text("Book a Growth Conversation:", M, y);
  doc.setTextColor(...TEAL);
  doc.textWithLink(contactInfo.bookingUrl, M + 44, y, { url: contactInfo.bookingUrl });
  y += 5;
  doc.setTextColor(...GREY);
  doc.text("Website:", M, y);
  doc.setTextColor(...TEAL);
  doc.textWithLink("teqie.co.uk", M + 15, y, { url: "https://teqie.co.uk" });

  doc.setFontSize(7.5);
  doc.setTextColor(...GREY);
  doc.text(
    `© ${new Date().getFullYear()} Teqie Ltd · Helping businesses work better, grow smarter and make better use of technology.`,
    M,
    290
  );

  const safeName = (businessName || firstName || "Teqie").replace(/[^a-z0-9]+/gi, "-");
  doc.save(`Teqie-Growth-Snapshot-${safeName}.pdf`);
}
